import { forwardRef } from 'react';
import { css } from '@emotion/react';
import { ActionTypeCreate, ActionTypeDelete, ActionTypeUpdate, type AuditEvent } from '@frontend/api-phone-audits';
import { Icon, type IconName } from '@frontend/icons';
import { Text } from '@frontend/design-system';
import { formatEventTime } from '../../utils/audit-formatters';
import { EventCard } from './event-card';

type EventListItemProps = {
  event: AuditEvent;
  isHighlighted?: boolean;
};

/** Diameter of the circular action marker. Exported for the parent list's line positioning. */
export const MARKER_SIZE = 32;

/**
 * Approximate height of the time label text row (small Text + gap below).
 * Used to push the marker circle down so it aligns with the top of the EventCard.
 * Exported for the parent list's line positioning.
 */
export const TIME_ROW_HEIGHT = 24;

type MarkerConfig = {
  iconName: IconName;
  /** CSS color value for the icon */
  iconColor: string;
  /** CSS background-color value for the circular marker. */
  bgColor: string;
};

/**
 * Returns the icon name, icon color, and background color for the timeline action marker.
 * - DELETE → trash icon, critical red tones
 * - CREATE → add icon, primary blue tones
 * - UPDATE → refresh icon, eggplant purple tones
 * - Unknown → warning-badge icon, warning yellow tones
 */
function getMarkerConfig(
  action: typeof ActionTypeDelete | typeof ActionTypeCreate | typeof ActionTypeUpdate | undefined
): MarkerConfig {
  switch (action) {
    case ActionTypeDelete:
      return {
        iconName: 'trash',
        iconColor: 'var(--colors-critical-50)',
        bgColor: 'var(--colors-critical-10)',
      };
    case ActionTypeCreate:
      return {
        iconName: 'add',
        iconColor: 'var(--colors-primary-50)',
        bgColor: 'var(--colors-primary-10)',
      };
    case ActionTypeUpdate:
      return {
        iconName: 'refresh',
        iconColor: 'var(--colors-eggplant-50)',
        bgColor: 'var(--colors-eggplant-10)',
      };
    default:
      return {
        iconName: 'warning-badge',
        iconColor: 'var(--fabric-color-status-warning-default)',
        bgColor: 'var(--fabric-color-status-warning-subtle)',
      };
  }
}

/**
 * A single row in the audit event timeline. Renders:
 * - A colored circular marker to the left, aligned with the top of the event card.
 * - A formatted timestamp above the event card.
 * - The EventCard itself
 */
export const EventListItem = forwardRef<HTMLLIElement, EventListItemProps>(({ event, isHighlighted }, ref) => {
  const marker = getMarkerConfig(event.action);

  const formattedTime = formatEventTime(event.createTime);

  return (
    <li ref={ref} css={baseItemStyles}>
      {/* Colored circular action marker (sits over the parent's continuous line) */}
      <div
        css={css`
          position: relative;
          /* Push the marker icon down to align the marker with the top of the card */
          top: ${TIME_ROW_HEIGHT}px;
          z-index: 1;
          width: ${MARKER_SIZE}px;
          height: ${MARKER_SIZE}px;
          border-radius: 50%;
          background-color: ${marker.bgColor};
          color: ${marker.iconColor};
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        `}
      >
        <Icon name={marker.iconName} size={16} />
      </div>

      {/* ---- Right column: timestamp + event card ---- */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: var(--fabric-space-gap-xs);
        `}
      >
        {/* Event timestamp, aligned to the left edge of the card */}
        <Text size='small' color='light'>
          {formattedTime}
        </Text>

        <EventCard event={event} isHighlighted={isHighlighted} />
      </div>
    </li>
  );
});

EventListItem.displayName = 'EventListItem';

const baseItemStyles = css`
  display: flex;
  flex-direction: row;
  gap: var(--fabric-space-gap-sm);
  list-style: none;
`;
